import { keepPreviousData, QueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";

/**
 * The app's one cache. A module singleton rather than state in a component, so
 * the auth gate (which sits above the provider's consumers) can clear it when the
 * signed-in person changes.
 *
 * - `staleTime` 30s: a view revisited within it renders from the cache and asks
 *   nothing; after it, it renders from the cache and refetches behind. The live
 *   feed (`live-sync.tsx`) invalidates what a write touched, so staleness is the
 *   safety net, not how changes arrive.
 * - `gcTime` 10min: how long a view nobody shows keeps its data for a return.
 * - A 4xx is an answer, not a blip — no retries for it; a network error or a 5xx
 *   (a server restarting) is retried twice with backoff before a view says so.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: true,
      retry: (failures, error) => !(error instanceof ApiError && error.status < 500) && failures < 2,
    },
    mutations: {
      retry: false,
    },
  },
});

/** For lists whose key carries a filter: the previous result stays up while the next loads. */
export const keepPrevious = keepPreviousData;

/** An error as one line for a view to show. */
export function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : error instanceof Error ? error.message || fallback : fallback;
}
